import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, type FeedbackPin } from '../types/review';

export function useReviewState(code?: string, planId?: string) {
  // Core state
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Plan/Item state
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [planItems, setPlanItems] = useState<any[]>([]);
  const [reviewType, setReviewType] = useState<'account_manager' | 'client'>('client');

  // Asset state
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState('all');

  // Feedback state
  const [feedback, setFeedback] = useState('');
  const [feedbackPins, setFeedbackPins] = useState<Record<number, FeedbackPin[]>>({});
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [tempPin, setTempPin] = useState<{ x: number; y: number } | null>(null);
  const [pinComment, setPinComment] = useState('');
  const [assetFeedback, setAssetFeedback] = useState<Record<number, string[]>>({});

  // Asset request/upload state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadAssetType, setUploadAssetType] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  const fetchReviewData = useCallback(async () => {
    try {
      let apiUrl;

      if (code && planId) {
        // URL structure - fetch plan-based review using the code (AM or Client code)
        apiUrl = `${API_BASE_URL}/api/plans/client/${code}/plan/${planId}`;
      } else if (code) {
        // Legacy URL structure - fetch single content review
        apiUrl = `${API_BASE_URL}/api/review/${code}`;
      } else {
        throw new Error('No valid review identifier found');
      }

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Invalid review link');
      }

      const data = await response.json();

      if (code && planId) {
        // Handle plan-based data
        console.log('Plan data received:', data);
        // Convert planId to number to match database IDs
        const numericPlanId = parseInt(planId);
        setSelectedPlan(String(numericPlanId));
        // Use the actual plans from the database
        const plans = data.availablePlans || [];
        setAvailablePlans(plans);
        setPlanItems(data.pages || []);
        if (data.pages && data.pages.length > 0) {
          const firstItem = data.pages[0];
          setSelectedItem(firstItem);
          setContent(firstItem);
          // Auto-select first asset if available
          if (firstItem.assets && firstItem.assets.length > 0) {
            setSelectedAsset(firstItem.assets[0]);
          }
        }
        setReviewType(data.reviewType);
      } else {
        // Handle legacy single content data
        setContent(data);
        setReviewType(data.reviewer_type === 'am' ? 'account_manager' : 'client');
        // Auto-select first asset if available
        if (data.assets && data.assets.length > 0) {
          setSelectedAsset(data.assets[0]);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch review data:', error);
      setError('Invalid review link or content not found.');
      setLoading(false);
    }
  }, [code, planId]);

  // Auto-select first asset when tab changes
  useEffect(() => {
    if (!content?.assets) return;

    // Get filtered assets based on current tab
    let filteredAssets = content.assets;
    if (selectedTab !== 'all') {
      const typeMap: Record<string, string[]> = {
        'content': ['page', 'copy'],
        'images': ['image', 'infographic'],
        'video': ['video', 'video_ad'],
        'seo': ['meta'],
        'schema': ['schema'],
        'media': ['podcast', 'song'],
        'audio': ['transcription'],
        'social': ['social'],
        'email': ['email'],
        'faq': ['faq'],
        // Digital Ads tabs
        'ad_creative': ['ad_creative', 'ad_banner', 'responsive_display', 'native_ad', 'html5_creative'],
        'ad_copy': ['ad_copy', 'search_ad', 'shopping_ad'],
        'ad_media': ['carousel', 'story_ad', 'collection_ad', 'message_ad', 'document_ad'],
      };
      const allowedTypes = typeMap[selectedTab] || [];
      filteredAssets = content.assets.filter((asset: any) => allowedTypes.includes(asset.asset_type));
    }

    // Auto-select first filtered asset if available
    // Only do this if no asset is selected, or if the selected asset is no longer in the filtered list
    // Check by ID to avoid reference equality issues after saves
    const selectedAssetStillExists = selectedAsset && filteredAssets.some((asset: any) => asset.id === selectedAsset.id);
    if (filteredAssets.length > 0 && !selectedAssetStillExists) {
      setSelectedAsset(filteredAssets[0]);
    } else if (selectedAssetStillExists && selectedAsset) {
      // Update the selected asset reference to the new one from filteredAssets to keep it in sync
      const updatedSelectedAsset = filteredAssets.find((asset: any) => asset.id === selectedAsset.id);
      if (updatedSelectedAsset) {
        setSelectedAsset(updatedSelectedAsset);
      }
    }
  }, [selectedTab, content]);

  const handlePlanChange = (newPlanId: string) => {
    // Update selected plan and navigate
    setSelectedPlan(newPlanId);

    // Navigate to the new plan URL
    // Note: This assumes the same review code works for all plans (for demo purposes)
    if (code && newPlanId) {
      window.location.href = `/${code}/review/${newPlanId}`;
    }
  };

  const handleItemSelect = (item: any) => {
    setSelectedItem(item);
    setContent(item);
    // Reset asset selection
    setSelectedAsset(null);
    setSelectedTab('all');
    // Auto-select first asset if available
    if (item.assets && item.assets.length > 0) {
      setSelectedAsset(item.assets[0]);
    }
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const handlePlacePin = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPlacingPin || !selectedAsset) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setTempPin({ x, y });
  };

  const handleAddPin = () => {
    if (!tempPin || !pinComment || !selectedAsset) return;

    const newPin: FeedbackPin = {
      id: Date.now().toString(),
      x: tempPin.x,
      y: tempPin.y,
      comment: pinComment,
      author: reviewType === 'account_manager' ? 'Account Manager' : 'Client',
      timestamp: new Date()
    };

    setFeedbackPins(prev => ({
      ...prev,
      [selectedAsset.id]: [...(prev[selectedAsset.id] || []), newPin]
    }));

    // Reset pin placement state
    setTempPin(null);
    setPinComment('');
    setIsPlacingPin(false);
  };

  const handleCancelPin = () => {
    setTempPin(null);
    setPinComment('');
    setIsPlacingPin(false);
  };

  const handleAddAssetFeedback = (assetId: number, comment: string) => {
    setAssetFeedback(prev => ({
      ...prev,
      [assetId]: [...(prev[assetId] || []), comment]
    }));
  };

  const handleApprove = async () => {
    if (!content?.id) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/review/approve`, {
        content_id: content.id,
        reviewer_type: reviewType,
        feedback: feedback || null
      });

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to approve:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!content?.id || !feedback.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/review/reject`, {
        content_id: content.id,
        reviewer_type: reviewType,
        feedback: feedback
      });

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to reject:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveAsset = async (assetId: number) => {
    try {
      await axios.post(`${API_BASE_URL}/api/assets/${assetId}/approve`, {
        approver_type: reviewType,
        approver_name: reviewType === 'account_manager' ? 'Account Manager' : 'Client',
        feedback: assetFeedback[assetId]?.join('\n') || null
      });

      // Refresh data
      await fetchReviewData();
    } catch (error) {
      console.error('Failed to approve asset:', error);
    }
  };

  const handleRejectAsset = async (assetId: number) => {
    const feedback = assetFeedback[assetId]?.join('\n');
    if (!feedback) return;

    try {
      await axios.post(`${API_BASE_URL}/api/assets/${assetId}/reject`, {
        approver_type: reviewType,
        approver_name: reviewType === 'account_manager' ? 'Account Manager' : 'Client',
        feedback
      });

      // Refresh data
      await fetchReviewData();
    } catch (error) {
      console.error('Failed to reject asset:', error);
    }
  };

  const handleEditAsset = (assetId: number) => {
    console.log('Edit asset functionality not yet implemented for asset:', assetId);
  };

  const handleDeleteAsset = async (assetId: number) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/assets/${assetId}`);

      // Refresh data to remove the deleted asset
      await fetchReviewData();

      // If the deleted asset was selected, select another one
      if (selectedAsset?.id === assetId) {
        const allAssets = planItems.length > 0
          ? planItems.flatMap((item: any) => item.assets || [])
          : assets;
        const remainingAssets = allAssets.filter((a: any) => a.id !== assetId);
        if (remainingAssets.length > 0) {
          setSelectedAsset(remainingAssets[0]);
        } else {
          setSelectedAsset(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset. Please try again.');
    }
  };

  const handleSaveAsset = async (assetId: number, content: string) => {
    try {
      // Store current selections and asset data before refresh
      const currentSelectedAssetId = selectedAsset?.id;
      const currentSelectedItemId = selectedItem?.id;

      // Store the current asset content to preserve after refresh
      const currentAsset = selectedAsset;

      await axios.put(`${API_BASE_URL}/api/assets/${assetId}`, {
        content
      });

      // Update the current asset's content immediately without full refresh
      if (currentAsset) {
        const updatedAsset = { ...currentAsset, content };
        setSelectedAsset(updatedAsset);

        // Update the asset in the current item's assets array
        if (selectedItem && selectedItem.assets) {
          const updatedAssets = selectedItem.assets.map((asset: any) =>
            asset.id === assetId ? updatedAsset : asset
          );
          const updatedItem = { ...selectedItem, assets: updatedAssets };
          setSelectedItem(updatedItem);
          setContent(updatedItem);

          // Update planItems if in plan mode
          if (planItems.length > 0) {
            const updatedPlanItems = planItems.map((item: any) =>
              item.id === selectedItem.id ? updatedItem : item
            );
            setPlanItems(updatedPlanItems);
          }
        }
      }

      // Don't refresh data to maintain current selection
      // The local state has been updated above

    } catch (error) {
      console.error('Failed to save asset:', error);
      throw error;
    }
  };

  const handleApproveAll = async () => {
    // If we're in plan mode (have planItems), approve all items
    if (planItems.length > 0) {
      const approvalPromises = planItems.map(async (item: any) => {
        if (item.assets && item.assets.length > 0) {
          const assetPromises = item.assets.map((asset: any) =>
            handleApproveAsset(asset.id).catch(error => ({ error, assetId: asset.id }))
          );
          return Promise.allSettled(assetPromises);
        }
        return Promise.resolve();
      });

      try {
        await Promise.all(approvalPromises);
        console.log('All items approved');
      } catch (error) {
        console.error('Failed to approve all items:', error);
      }
    } else {
      // Single content mode - approve all assets in current content
      if (!content?.assets) return;

      const approvalPromises = content.assets.map((asset: any) =>
        handleApproveAsset(asset.id).catch(error => ({ error, assetId: asset.id }))
      );

      try {
        await Promise.allSettled(approvalPromises);
        console.log('All assets approved');
      } catch (error) {
        console.error('Failed to approve all assets:', error);
      }
    }
  };

  const handleNext = () => {
    if (!content?.assets || !selectedAsset) return;

    const currentAssetIndex = content.assets.findIndex((a: any) => a.id === selectedAsset.id);

    // Check if there's a next asset in the current item
    if (currentAssetIndex < content.assets.length - 1) {
      // Move to next asset
      const nextAsset = content.assets[currentAssetIndex + 1];
      setSelectedAsset(nextAsset);
    } else if (planItems.length > 0 && selectedItem) {
      // No more assets in current item, move to next item
      const currentItemIndex = planItems.findIndex(item => item.id === selectedItem.id);

      if (currentItemIndex < planItems.length - 1) {
        // Move to next item
        const nextItem = planItems[currentItemIndex + 1];
        handleItemSelect(nextItem);
      }
    }
  };

  return {
    // State
    content,
    loading,
    error,
    submitted,
    submitting,
    selectedPlan,
    availablePlans,
    selectedItem,
    planItems,
    reviewType,
    selectedAsset,
    selectedTab,
    feedback,
    feedbackPins,
    isPlacingPin,
    tempPin,
    pinComment,
    assetFeedback,
    showRequestModal,
    requestType,
    requestDescription,
    showAddAssetModal,
    uploadFile,
    uploadAssetType,
    uploadDescription,
    isUploading,
    wizardStep,

    // Setters
    setFeedback,
    setSelectedAsset,
    setIsPlacingPin,
    setPinComment,
    setShowRequestModal,
    setRequestType,
    setRequestDescription,
    setShowAddAssetModal,
    setUploadFile,
    setUploadAssetType,
    setUploadDescription,
    setIsUploading,
    setWizardStep,

    // Handlers
    fetchReviewData,
    handlePlanChange,
    handleItemSelect,
    handleTabChange,
    handlePlacePin,
    handleAddPin,
    handleCancelPin,
    handleAddAssetFeedback,
    handleApprove,
    handleReject,
    handleApproveAsset,
    handleRejectAsset,
    handleEditAsset,
    handleDeleteAsset,
    handleSaveAsset,
    handleApproveAll,
    handleNext
  };
}